import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { PIPELINES_ORIGIN } from "../../lib/aiApiBaseUrl"

/** Some endpoints wrap the row in `{ data: { … } }`. */
function normalizePreferenceResponse(data) {
  if (data == null || typeof data !== "object") return data
  const inner = data.data
  if (inner && typeof inner === "object" && !Array.isArray(inner)) return inner
  return data
}

/** Shallow snapshot of fields used for PATCH diff (GET / POST / PATCH responses). */
export function snapshotPlannerPreferenceFields(pref) {
  if (!pref || typeof pref !== "object") return null
  return {
    startDate: pref.startDate,
    endDate: pref.endDate,
    eventIds: [...(pref.eventIds || [])],
    contentPillarIds: [...(pref.contentPillarIds || [])],
    socialNetworkTypeIds: [...(pref.socialNetworkTypeIds || [])],
    publishingStyles: [...(pref.publishingStyles || [])],
    mediaAssetIds: [...(pref.mediaAssetIds || [])],
  }
}

const ymdSlice = (d) => {
  if (d == null || d === "") return ""
  const s = String(d)
  return s.includes("T") ? s.slice(0, 10) : s.slice(0, 10)
}

function comparablePlannerPreferenceFields(pref) {
  if (!pref || typeof pref !== "object") return null
  const sortNums = (arr) =>
    [...(arr || [])]
      .map((x) => Number(x))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b)
      .join("|")
  const sortStr = (arr) =>
    [...(arr || [])]
      .map((x) => String(x).trim().toLowerCase())
      .filter(Boolean)
      .sort()
      .join("|")
  return {
    startDate: ymdSlice(pref.startDate),
    endDate: ymdSlice(pref.endDate),
    eventIds: sortNums(pref.eventIds),
    contentPillarIds: sortNums(pref.contentPillarIds),
    socialNetworkTypeIds: sortNums(pref.socialNetworkTypeIds),
    publishingStyles: sortStr(pref.publishingStyles),
    mediaAssetIds: sortNums(pref.mediaAssetIds),
  }
}

/**
 * PATCH body: only keys whose normalized values differ from the last server snapshot.
 * If nothing changed, returns `{}` (caller may skip the request).
 */
export function diffPlannerPreferencePayload(baselineSnapshot, fullPayload) {
  if (!baselineSnapshot || typeof baselineSnapshot !== "object") return fullPayload
  if (!fullPayload || typeof fullPayload !== "object") return fullPayload
  const b = comparablePlannerPreferenceFields(baselineSnapshot)
  const n = comparablePlannerPreferenceFields(fullPayload)
  if (!b || !n) return fullPayload
  const patch = {}
  if (b.startDate !== n.startDate) patch.startDate = fullPayload.startDate
  if (b.endDate !== n.endDate) patch.endDate = fullPayload.endDate
  if (b.eventIds !== n.eventIds) patch.eventIds = fullPayload.eventIds
  if (b.contentPillarIds !== n.contentPillarIds) patch.contentPillarIds = fullPayload.contentPillarIds
  if (b.socialNetworkTypeIds !== n.socialNetworkTypeIds) patch.socialNetworkTypeIds = fullPayload.socialNetworkTypeIds
  if (b.publishingStyles !== n.publishingStyles) patch.publishingStyles = fullPayload.publishingStyles
  if (b.mediaAssetIds !== n.mediaAssetIds) patch.mediaAssetIds = fullPayload.mediaAssetIds
  return patch
}

function getBearerToken(getState) {
  const state = getState?.() || {}
  const auth = state.auth || {}
  return (
    auth.token ||
    auth.accessToken ||
    auth.user?.token ||
    (typeof localStorage !== "undefined" ? localStorage.getItem("token") : null)
  )
}

async function aiPlannerPreferencesRequest(method, path, getState, body) {
  const base = PIPELINES_ORIGIN
  if (!base) {
    throw new Error("Pipelines URL is not configured.")
  }
  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`
  const token = getBearerToken(getState)
  const headers = {
    Accept: "*/*",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(body != null ? { "Content-Type": "application/json" } : {}),
  }
  const response = await fetch(url, {
    method,
    headers,
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  })
  const text = await response.text()
  let data = null
  if (text?.trim()) {
    try {
      data = JSON.parse(text)
    } catch {
      data = { raw: text }
    }
  }
  if (response.status === 404 && method === "GET") {
    return null
  }
  if (!response.ok) {
    const msg = data?.error || data?.message || `Request failed (${response.status})`
    throw new Error(msg)
  }
  /** Some pipelines routes return HTTP 200 with `{ success: false, code: "…not found" }` instead of 404. */
  if (method === "GET" && data && data.success === false) {
    const errText = `${data.code || ""} ${data.message || ""}`.toLowerCase()
    if (errText.includes("not found")) {
      return null
    }
  }
  if (data && data.ok === false) {
    throw new Error(data.error || "AI Planner preferences request failed")
  }
  if (data && data.success === false) {
    throw new Error(data.message || data.error || data.code || "AI Planner preferences request failed")
  }
  return data
}

/**
 * GET saved AI planner preferences for a client (pipelines host).
 * Returns `null` when none exist (HTTP 404).
 */
export const fetchAiPlannerPreferences = createAsyncThunk(
  "aiPlannerPreferences/fetch",
  async ({ clientId }, { getState, rejectWithValue }) => {
    try {
      if (clientId == null || String(clientId).trim() === "") {
        return rejectWithValue("Missing client id")
      }
      const path = `/api/v1/ai-planner-preferences/clients/${encodeURIComponent(String(clientId))}`
      const data = await aiPlannerPreferencesRequest("GET", path, getState, undefined)
      return { clientId: Number(clientId), preference: data == null ? null : normalizePreferenceResponse(data) }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to load AI planner preferences")
    }
  },
)

/** GET a single preferences row by id (`/api/v1/ai-planner-preferences/{id}`). */
export const fetchAiPlannerPreferencesById = createAsyncThunk(
  "aiPlannerPreferences/fetchById",
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      if (id == null || String(id).trim() === "") {
        return rejectWithValue("Missing preferences id")
      }
      const path = `/api/v1/ai-planner-preferences/${encodeURIComponent(String(id))}`
      const data = await aiPlannerPreferencesRequest("GET", path, getState, undefined)
      return { preference: data == null ? null : normalizePreferenceResponse(data) }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to load AI planner preferences by id")
    }
  },
)

/** First-time save when the property has no preferences row yet. */
export const createAiPlannerPreferences = createAsyncThunk(
  "aiPlannerPreferences/create",
  async ({ clientId, body }, { getState, rejectWithValue }) => {
    try {
      if (clientId == null || String(clientId).trim() === "") {
        return rejectWithValue("Missing client id")
      }
      const path = `/api/v1/ai-planner-preferences/clients/${encodeURIComponent(String(clientId))}`
      const data = await aiPlannerPreferencesRequest("POST", path, getState, body)
      return { clientId: Number(clientId), preference: normalizePreferenceResponse(data) }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to save AI planner preferences")
    }
  },
)

/** Update after the user changes planning settings and regenerates. */
export const updateAiPlannerPreferences = createAsyncThunk(
  "aiPlannerPreferences/update",
  async ({ id, body }, { getState, rejectWithValue }) => {
    try {
      if (id == null || String(id).trim() === "") {
        return rejectWithValue("Missing preferences id")
      }
      const path = `/api/v1/ai-planner-preferences/${encodeURIComponent(String(id))}`
      const data = await aiPlannerPreferencesRequest("PATCH", path, getState, body)
      return { preference: normalizePreferenceResponse(data) }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update AI planner preferences")
    }
  },
)

const initialState = {
  preference: null,
  /** Last server-aligned field snapshot for PATCH partial updates (GET / save success). */
  baselinePreference: null,
  /** `clientId` for the last successful GET (or create) */
  lastClientId: null,
  status: "idle",
  error: null,
  mutationStatus: "idle",
  mutationError: null,
}

const aiPlannerPreferencesSlice = createSlice({
  name: "aiPlannerPreferences",
  initialState,
  reducers: {
    clearAiPlannerPreferences: () => ({ ...initialState }),
    /** Merge fields after local edits without a round-trip (optional). */
    setAiPlannerPreferenceLocal: (state, action) => {
      if (!action.payload || typeof action.payload !== "object") return
      state.preference = { ...(state.preference || {}), ...action.payload }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAiPlannerPreferences.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchAiPlannerPreferences.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.lastClientId = action.payload.clientId
        state.preference = action.payload.preference
        state.baselinePreference = snapshotPlannerPreferenceFields(action.payload.preference)
        state.error = null
      })
      .addCase(fetchAiPlannerPreferences.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
      .addCase(fetchAiPlannerPreferencesById.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchAiPlannerPreferencesById.fulfilled, (state, action) => {
        state.status = "succeeded"
        const pref = action.payload.preference
        state.preference = pref
        state.baselinePreference = snapshotPlannerPreferenceFields(pref)
        if (pref && pref.clientId != null && String(pref.clientId).trim() !== "") {
          const n = Number(pref.clientId)
          state.lastClientId = Number.isNaN(n) ? state.lastClientId : n
        }
        state.error = null
      })
      .addCase(fetchAiPlannerPreferencesById.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
      .addCase(createAiPlannerPreferences.pending, (state) => {
        state.mutationStatus = "loading"
        state.mutationError = null
      })
      .addCase(createAiPlannerPreferences.fulfilled, (state, action) => {
        state.mutationStatus = "succeeded"
        const next = action.payload.preference
        const prev = state.preference
        state.preference =
          prev && next && typeof prev === "object" && typeof next === "object" ? { ...prev, ...next } : next
        state.baselinePreference = snapshotPlannerPreferenceFields(state.preference)
        state.lastClientId = action.payload.clientId
        state.mutationError = null
      })
      .addCase(createAiPlannerPreferences.rejected, (state, action) => {
        state.mutationStatus = "failed"
        state.mutationError = action.payload
      })
      .addCase(updateAiPlannerPreferences.pending, (state) => {
        state.mutationStatus = "loading"
        state.mutationError = null
      })
      .addCase(updateAiPlannerPreferences.fulfilled, (state, action) => {
        state.mutationStatus = "succeeded"
        const next = action.payload.preference
        const prev = state.preference
        state.preference =
          prev && next && typeof prev === "object" && typeof next === "object" ? { ...prev, ...next } : next
        state.baselinePreference = snapshotPlannerPreferenceFields(state.preference)
        state.mutationError = null
      })
      .addCase(updateAiPlannerPreferences.rejected, (state, action) => {
        state.mutationStatus = "failed"
        state.mutationError = action.payload
      })
  },
})

export const { clearAiPlannerPreferences, setAiPlannerPreferenceLocal } = aiPlannerPreferencesSlice.actions

export default aiPlannerPreferencesSlice.reducer
